import { Button, ButtonTheme } from 'components/Button';
import { OrganisationFilter } from 'components/Filters/OrganisationFilter';
import { Highlight } from 'components/Highlight';
import { ListSelectAllCheckbox } from 'components/ListSelectAllCheckbox';
import { SortMenu } from 'components/SortMenu';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  CHOOSE_ASSETS,
  CLEAR_CHOSEN_ASSETS,
  DELETE_CHOSEN_ASSETS,
  SET_ASSETS_FILTER,
  SET_ASSETS_SORT,
} from 'store/actions';
import {
  selectAssetListSort,
  selectChosenAssetsCount,
  selectFilteredAssetsListIds,
  selectOrganisationFilterValue,
} from 'store/assets/selectors';
import { AssetsSortOrder } from 'store/assets/types';
import { selectOrganisationFilterOptions } from 'store/organisations/selectors';

interface SortOption {
  id: AssetsSortOrder;
  name: string;
}

export const Filters: React.FC = () => {
  const dispatch = useDispatch();

  const options: SortOption[] = [
    { id: AssetsSortOrder.NAMEASC, name: 'Name A-Z' },
    { id: AssetsSortOrder.NAMEDESC, name: 'Name Z-A' },
    { id: AssetsSortOrder.ASSESSMENTDUEASC, name: 'Assessment Due' },
  ];

  const assetsListSort = useSelector(selectAssetListSort);
  const organisationFilterOptions = useSelector(
    selectOrganisationFilterOptions,
  );
  const organisationFilterValue = useSelector(selectOrganisationFilterValue);

  const filteredAssetListIds = useSelector(selectFilteredAssetsListIds);
  const chosenAssetsCount = useSelector(selectChosenAssetsCount);

  const onChange = (id: string) =>
    dispatch(SET_ASSETS_SORT(id as AssetsSortOrder));

  const handleOrganisationFilterChange = (id: string) => {
    dispatch(SET_ASSETS_FILTER({ organisations: id === '0' ? [] : [id] }));
  };

  return (
    <View style={styles.view}>
      <ListSelectAllCheckbox
        onSelectAll={() => dispatch(CHOOSE_ASSETS(filteredAssetListIds))}
        onSelectNone={() => dispatch(CLEAR_CHOSEN_ASSETS())}
        selectionState={
          !chosenAssetsCount
            ? 'none'
            : filteredAssetListIds.length !== chosenAssetsCount
              ? 'some'
              : 'all'
        }
      />
      {!!chosenAssetsCount && (
        <Highlight highlight style={{ borderRadius: 4 }}>
          <Button
            text="Remove Assets from Device"
            onPress={() => dispatch(DELETE_CHOSEN_ASSETS())}
            buttonTheme={ButtonTheme.secondary}
          />
        </Highlight>
      )}
      {!chosenAssetsCount && (
        <ViewWithSeparators
          horizontal
          separatorMargin={theme.paddingHorizontalHalf}
          separatorColor="transparent"
        >
          {organisationFilterOptions.length > 1 && (
            <OrganisationFilter
              value={organisationFilterValue}
              options={[
                { id: '0', name: 'Filter Off' },
                ...organisationFilterOptions,
              ]}
              onChange={handleOrganisationFilterChange}
              disabled={!!chosenAssetsCount}
            />
          )}
          <SortMenu
            value={assetsListSort}
            options={options}
            onChange={onChange}
            disabled={!!chosenAssetsCount}
          />
        </ViewWithSeparators>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  view: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
